(() => {
  const DESKTOP_BREAKPOINT = 750;

  function lockBodyScroll() {
    if (document.body.dataset.pmDialogLocked === 'true') return;
    document.body.dataset.pmDialogLocked = 'true';
    document.body.dataset.pmDialogScrollY = String(window.scrollY);
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;
  }

  function unlockBodyScroll() {
    if (document.body.dataset.pmDialogLocked !== 'true') return;
    const previousScrollY = Number(document.body.dataset.pmDialogScrollY || 0);
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    delete document.body.dataset.pmDialogLocked;
    delete document.body.dataset.pmDialogScrollY;
    window.scrollTo({ top: previousScrollY, behavior: 'instant' });
  }

  function getDialogHost(dialog) {
    return dialog?.closest?.('[data-product-media-showcase]') || null;
  }

  function activateDialogHost(dialog) {
    if (window.innerWidth >= DESKTOP_BREAKPOINT) return null;
    const host = getDialogHost(dialog);
    host?.classList.add('is-mobile-dialog-host-active');
    return host;
  }

  function deactivateDialogHost(dialog) {
    if (window.innerWidth >= DESKTOP_BREAKPOINT) return;
    const host = getDialogHost(dialog);
    host?.classList.remove('is-mobile-dialog-host-active');
  }

  function force360HintVisible(dialog) {
    const hint = dialog?.querySelector?.('[data-360-hint]');
    if (!hint) return;

    hint.classList.remove('is-hidden');
    hint.style.display = 'flex';
    hint.style.visibility = 'visible';
    hint.style.opacity = '1';

    const hintImage = hint.querySelector('img');
    if (hintImage) {
      hintImage.loading = 'eager';
      hintImage.decoding = 'sync';
      hintImage.style.display = 'block';
      hintImage.style.opacity = '1';
      hintImage.style.visibility = 'visible';
    }
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open) return false;
    const host = activateDialogHost(dialog);
    lockBodyScroll();
    try {
      dialog.showModal();
      if (dialog.hasAttribute('data-360-dialog')) {
        force360HintVisible(dialog);
        window.setTimeout(() => force360HintVisible(dialog), 80);
      }
      return true;
    } catch (error) {
      host?.classList.remove('is-mobile-dialog-host-active');
      unlockBodyScroll();
      console.error('Showcase dialog open error:', error);
      return false;
    }
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
    deactivateDialogHost(dialog);
    unlockBodyScroll();
  }

  function parseFrames(raw) {
    if (!raw) return [];

    const value = String(raw).trim();
    if (!value) return [];

    if (value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item).trim()).filter((item) => item.startsWith('http'));
        }
      } catch (_) {}
    }

    return value
      .split(/[\n\r,\s]+/)
      .map((item) => item.trim().replace(/^["'\s]+|["'\s]+$/g, ''))
      .filter((item) => item.length > 5 && item.startsWith('http'));
  }

  function normalizeBundleKey(value) {
    const bundleValue = String(value || '').toLowerCase();
    if (bundleValue.includes('basic')) return 'basic';
    if (bundleValue.includes('standard')) return 'standard';
    if (bundleValue.includes('pro')) return 'pro';
    return '';
  }

  function normalizeColorKey(value) {
    const colorValue = String(value || '').toLowerCase().trim();
    if (!colorValue) return '';
    if (
      colorValue.includes('black') ||
      colorValue.includes('jet') ||
      colorValue.includes('midnight')
    ) {
      return 'black';
    }
    if (
      colorValue.includes('grey') ||
      colorValue.includes('gray') ||
      colorValue.includes('silver') ||
      colorValue.includes('space')
    ) {
      return 'grey';
    }
    if (colorValue.includes('green') || colorValue.includes('moss')) {
      return 'green';
    }
     if (colorValue.includes('glacier')) {
      return 'glacier';
    }
    if (colorValue.includes('graphite')) {
      return 'graphite';
    }
    if (colorValue.includes('obsidian')) {
      return 'obsidian';
    }
    return '';
  }

  class ProductMedia360Viewer {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('[data-360-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.hint = root.querySelector('[data-360-hint]');
      this.loadedFrames = [];
      this.frameCount = 0;
      this.currentFrame = 0;
      this.dragAccumulator = 0;
      this.isDragging = false;
      this.lastX = 0;
      this.singleImage = null;
      this.previewUrl = root.dataset.previewUrl || '';
      this.framesRaw = root.dataset.framesRaw || '';

      if (!this.canvas || !this.ctx) return;

      this.bind();
      this.reset({
        previewUrl: this.previewUrl,
        framesRaw: this.framesRaw,
      });
    }

    hideHint() {
      if (!this.hint) return;
      this.hint.classList.add('is-hidden');
      this.hint.style.opacity = '0';
      this.hint.style.visibility = 'hidden';
    }

    bind() {
      window.addEventListener('mousemove', (event) => {
        if (!this.isDragging) return;
        this.handleDrag(event.clientX);
      });

      window.addEventListener('mouseup', () => {
        this.isDragging = false;
      });

      this.root.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.isDragging = true;
        this.lastX = event.clientX;
        this.hideHint();
      });

      this.root.addEventListener(
        'touchstart',
        (event) => {
          this.isDragging = true;
          this.lastX = event.touches[0].clientX;
          this.hideHint();
        },
        { passive: true }
      );

      this.root.addEventListener(
        'touchmove',
        (event) => {
          if (!this.isDragging) return;
          event.preventDefault();
          this.handleDrag(event.touches[0].clientX);
        },
        { passive: false }
      );

      this.root.addEventListener('touchend', () => {
        this.isDragging = false;
      });

      window.addEventListener('resize', () => {
        this.sizeCanvas();
        this.drawCurrent();
      });
    }

    sizeCanvas() {
      const rect = this.root.getBoundingClientRect();
      const width = Math.max(Math.round(rect.width), 1);
      const height = Math.max(Math.round(rect.height), 1);

      if (this.canvas.width === width && this.canvas.height === height) return;

      this.canvas.width = width;
      this.canvas.height = height;
    }

    drawImage(image) {
      if (!image || !image.naturalWidth) return;
      this.sizeCanvas();

      const { width, height } = this.canvas;
      const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * scale;
      const drawHeight = image.naturalHeight * scale;
      const x = (width - drawWidth) / 2;
      const y = (height - drawHeight) / 2;

      this.ctx.clearRect(0, 0, width, height);
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';
      this.ctx.drawImage(image, x, y, drawWidth, drawHeight);
    }

    drawCurrent() {
      if (this.loadedFrames[this.currentFrame]) {
        this.drawImage(this.loadedFrames[this.currentFrame]);
        return;
      }

      if (this.singleImage) {
        this.drawImage(this.singleImage);
      }
    }

    handleDrag(clientX) {
      const delta = clientX - this.lastX;
      this.lastX = clientX;

      this.hideHint();

      if (this.frameCount > 1) {
        this.dragAccumulator += delta;
        const threshold = 5;
        const steps = Math.trunc(this.dragAccumulator / threshold);

        if (steps !== 0) {
          this.dragAccumulator -= steps * threshold;
          this.currentFrame =
            ((this.currentFrame - steps) % this.frameCount + this.frameCount) % this.frameCount;
          this.drawCurrent();
        }
      }
    }

    loadImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    }

    async preloadFrames(previewUrl, framesRaw) {
      const frameUrls = parseFrames(framesRaw);

      if (!frameUrls.length) {
        if (previewUrl) {
          this.singleImage = await this.loadImage(previewUrl).catch(() => null);
          this.drawCurrent();
        }
        return;
      }

      this.frameCount = frameUrls.length;
      const results = await Promise.allSettled(frameUrls.map((url) => this.loadImage(url)));
      this.loadedFrames = results
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .filter(Boolean);

      if (this.loadedFrames.length) {
        this.frameCount = this.loadedFrames.length;
        this.currentFrame = 0;
        this.drawCurrent();
      } else if (previewUrl) {
        this.singleImage = await this.loadImage(previewUrl).catch(() => null);
        this.drawCurrent();
      }
    }

    reset({ previewUrl = '', framesRaw = '' }) {
      this.previewUrl = previewUrl;
      this.framesRaw = framesRaw;
      this.loadedFrames = [];
      this.frameCount = 0;
      this.currentFrame = 0;
      this.dragAccumulator = 0;
      this.singleImage = null;
      if (this.hint) this.hint.classList.remove('is-hidden');
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.preloadFrames(this.previewUrl, this.framesRaw);
    }

    showHint() {
      if (!this.hint) return;
      this.hint.classList.remove('is-hidden');
      this.hint.style.display = 'flex';
      this.hint.style.visibility = 'visible';
      this.hint.style.opacity = '1';
      const hintImage = this.hint.querySelector('img');
      if (hintImage) {
        hintImage.style.display = 'block';
        hintImage.style.visibility = 'visible';
        hintImage.style.opacity = '1';
      }
    }
  }

  class ProductMediaShowcase {
    constructor(root) {
      this.root = root;
      this.root.productMediaShowcase = this;
      this.panes = Array.from(root.querySelectorAll('[data-showcase-pane]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-showcase-thumb]'));
      this.prevButton = root.querySelector('[data-showcase-prev]');
      this.nextButton = root.querySelector('[data-showcase-next]');
      this.zoomButton = root.querySelector('[data-open-zoom]');
      this.videoButton = root.querySelector('[data-open-video]');
      this.viewerButton = root.querySelector('[data-open-360]');
      this.thumbViewport = root.querySelector('[data-thumb-viewport]');
      this.thumbUp = root.querySelector('[data-thumb-scroll="up"]');
      this.thumbDown = root.querySelector('[data-thumb-scroll="down"]');
      this.videoDialog = root.querySelector('[data-video-dialog]');
      this.viewerDialog = root.querySelector('[data-360-dialog]');
      this.fullscreenDialog = root.querySelector('[data-fullscreen-dialog]');
      this.fullscreenPanes = Array.from(root.querySelectorAll('[data-fullscreen-pane]'));
      this.fullscreenThumbs = Array.from(root.querySelectorAll('[data-fullscreen-thumb]'));
      this.fullscreenPrev = root.querySelector('[data-fullscreen-prev]');
      this.fullscreenNext = root.querySelector('[data-fullscreen-next]');
      this.viewerRoot = root.querySelector('[data-360-root]');
      this.currentIndex = Number(root.dataset.initialIndex || 0);
      this.thumbnailPosition = root.dataset.thumbnailPosition || 'left';
      this.viewer = this.viewerRoot ? new ProductMedia360Viewer(this.viewerRoot) : null;
      this.currentColorKey = normalizeColorKey(
        window.currentProductColor ||
          document.documentElement.dataset.currentProductColor ||
          root.dataset.currentColorKey ||
          window.location.pathname
      );

      if (!this.panes.length) return;

      this.bind();
      this.show(this.currentIndex, false);
      this.syncBundleSelection();
      this.syncVariantState();
    }

    bind() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.show(Number(thumb.dataset.showcaseThumb || 0), true);
        });
      });

      this.prevButton?.addEventListener('click', () => {
        this.show(this.currentIndex - 1, true);
      });

      this.nextButton?.addEventListener('click', () => {
        this.show(this.currentIndex + 1, true);
      });

      this.zoomButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.showFullscreen(this.currentIndex, false);
        openDialog(this.fullscreenDialog);
      });

      this.videoButton?.addEventListener('click', () => {
        openDialog(this.videoDialog);
        window.setTimeout(() => {
          const video = this.videoDialog?.querySelector('video');
          if (video) {
            video.setAttribute('controls', 'controls');
            video.controls = true;
            video.currentTime = 0;
            video.play().catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }

          const iframe = this.videoDialog?.querySelector('iframe');
          if (iframe) {
            const autoplaySrc = iframe.dataset.autoplaySrc || iframe.dataset.baseSrc || iframe.src;
            if (autoplaySrc) iframe.src = autoplaySrc;
          }
        }, 120);
      });

      this.viewerButton?.addEventListener('click', () => {
        if (this.viewer) {
          this.viewer.reset({
            previewUrl: this.activePreviewUrl,
            framesRaw: this.active360FramesRaw,
          });
          this.viewer.showHint();
        }
        openDialog(this.viewerDialog);
      });

      this.thumbUp?.addEventListener('click', () => {
        this.scrollThumbs(-1);
      });

      this.thumbDown?.addEventListener('click', () => {
        this.scrollThumbs(1);
      });

      this.fullscreenThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.showFullscreen(Number(thumb.dataset.fullscreenThumb || 0), true);
        });
      });

      this.fullscreenPrev?.addEventListener('click', () => {
        this.showFullscreen(this.currentIndex - 1, true);
      });

      this.fullscreenNext?.addEventListener('click', () => {
        this.showFullscreen(this.currentIndex + 1, true);
      });

      this.root.querySelectorAll('[data-dialog-close]').forEach((button) => {
        button.addEventListener('click', () => {
          const dialog = button.closest('dialog');
          closeDialog(dialog);
          const video = this.videoDialog?.querySelector('video');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
          const iframe = this.videoDialog?.querySelector('iframe');
          if (dialog === this.videoDialog && iframe?.dataset.baseSrc) {
            iframe.src = iframe.dataset.baseSrc;
          }
        });
      });

      this.root.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          const rect = dialog.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;

          if (!inside) {
            closeDialog(dialog);
            const video = this.videoDialog?.querySelector('video');
            if (video) {
              video.pause();
              video.currentTime = 0;
            }
            const iframe = this.videoDialog?.querySelector('iframe');
            if (dialog === this.videoDialog && iframe?.dataset.baseSrc) {
              iframe.src = iframe.dataset.baseSrc;
            }
          }
        });

        dialog.addEventListener('close', () => {
          deactivateDialogHost(dialog);
          unlockBodyScroll();
        });
      });

      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.closest('fieldset.variant-option--bundle-plan')) {
          this.syncBundleSelection();
        }

        if (target.closest('variant-picker, form, .product-details')) {
          window.requestAnimationFrame(() => {
            this.syncVariantState();
          });
        }
      });

      document.addEventListener('variant:change', (event) => {
        if (event?.detail?.color) {
          window.currentProductColor = event.detail.color;
          document.documentElement.dataset.currentProductColor = event.detail.color;
        }
        window.setTimeout(() => this.syncVariantState(), 120);
      });

      document.addEventListener('variant:update', (event) => {
        const detailColor = event?.detail?.color || event?.detail?.resource?.color;
        if (detailColor) {
          window.currentProductColor = detailColor;
          document.documentElement.dataset.currentProductColor = detailColor;
        }
        window.setTimeout(() => this.syncVariantState(), 120);
      });

      document.addEventListener('click', (event) => {
        const swatch = event.target.closest(
          '.color-variant-swatch[data-color], .winning-team-swatch[data-color]'
        );
        if (!swatch?.dataset?.color) return;

        window.currentProductColor = swatch.dataset.color;
        document.documentElement.dataset.currentProductColor = swatch.dataset.color;

        window.setTimeout(() => this.syncVariantState(), 0);
        window.setTimeout(() => this.syncVariantState(), 150);
      });
    }

    scrollThumbs(direction) {
      if (!this.thumbViewport) return;

      const isBottom = this.thumbnailPosition === 'bottom';
      if (isBottom) {
        this.thumbViewport.scrollBy({ left: direction * 320, behavior: 'smooth' });
        return;
      }

      this.thumbViewport.scrollBy({ top: direction * 320, behavior: 'smooth' });
    }

    show(index, scrollThumb) {
      const count = this.panes.length;
      this.currentIndex = ((index % count) + count) % count;

      this.panes.forEach((pane, paneIndex) => {
        pane.classList.toggle('is-active', paneIndex === this.currentIndex);
      });

      this.thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === this.currentIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');

        if (isActive && scrollThumb) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });

      this.syncFullscreenState(scrollThumb);
    }

    showFullscreen(index, scrollThumb) {
      if (!this.fullscreenPanes.length) return;
      const count = this.fullscreenPanes.length;
      this.currentIndex = ((index % count) + count) % count;
      this.show(this.currentIndex, false);
      this.syncFullscreenState(scrollThumb);
    }

    syncFullscreenState(scrollThumb) {
      if (!this.fullscreenPanes.length) return;

      this.fullscreenPanes.forEach((pane, paneIndex) => {
        pane.classList.toggle('is-active', paneIndex === this.currentIndex);
      });

      this.fullscreenThumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === this.currentIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');

        if (isActive && scrollThumb) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
      });
    }

    syncBundleSelection() {
      const checkedBundle = document.querySelector(
        'fieldset.variant-option--bundle-plan input[type="radio"]:checked'
      );
      const bundleKey = normalizeBundleKey(checkedBundle?.value || '');
      if (!bundleKey) return;

      const matchedIndex = this.panes.findIndex((pane) => {
        const paneBundle = pane.dataset.bundleKey || '';
        return paneBundle === bundleKey;
      });

      if (matchedIndex >= 0) {
        this.show(matchedIndex, true);
      }
    }

    syncVariantState() {
      this.currentColorKey = this.getCurrentColorKey();
      this.root.dataset.currentColorKey = this.currentColorKey;

      const framesRaw = this.getFramesForColor(this.currentColorKey);
      this.root.dataset.framesRaw = framesRaw;

      this.panes.forEach((pane) => {
        pane.dataset.framesRaw = framesRaw;
      });

      if (this.viewer && this.viewerDialog?.open) {
        this.viewer.reset({
          previewUrl: this.activePreviewUrl,
          framesRaw,
        });
      }
    }

    getCurrentColorKey() {
      const scope =
        this.root.closest('.product-information__grid, .shopify-section, main, body') || document;

      const scopedDetails =
        scope.querySelector('.product-information__details, .product-details') || scope;
      const checkedInput = scope.querySelector(
        'fieldset.variant-option--swatches input[type="radio"]:checked, input[name*="Color"]:checked, input[name*="color"]:checked, input[name*="colour"]:checked'
      );
      const pressedLabel = scope.querySelector(
        'fieldset.variant-option--swatches .variant-option__button-label[aria-pressed="true"]'
      );
      const customActiveSwatch = scopedDetails.querySelector(
        '.color-variant-swatch.is-active, .winning-team-swatch.is-active'
      );

      const candidate =
        customActiveSwatch?.dataset.color ||
        checkedInput?.value ||
        checkedInput?.dataset.colorName ||
        pressedLabel?.textContent ||
        document.documentElement.dataset.currentProductColor ||
        window.currentProductColor ||
        this.root.dataset.currentColorKey ||
        '';

      return normalizeColorKey(candidate) || normalizeColorKey(window.location.pathname);
    }

    getFramesForColor(colorKey) {
      const preferredKey = colorKey || this.currentColorKey || '';
      const rootData = this.root.dataset;

      if (preferredKey === 'green' && rootData.framesGreen) return rootData.framesGreen;
      if (preferredKey === 'grey' && rootData.framesGrey) return rootData.framesGrey;
      if (preferredKey === 'black' && rootData.framesBlack) return rootData.framesBlack;

      return (
        rootData.framesBlack || rootData.framesGrey || rootData.framesGreen || rootData.framesRaw || ''
      );
    }

    get activePreviewUrl() {
      const activePane = this.panes[this.currentIndex];
      const image = activePane?.querySelector('img');
      return image?.currentSrc || image?.src || this.root.dataset.previewUrl || '';
    }

    get active360FramesRaw() {
      return this.getFramesForColor(this.currentColorKey || this.getCurrentColorKey());
    }
  }

  function syncSupportFeatures() {
    document.querySelectorAll('[data-support-features-block]').forEach((block) => {
      if (block.dataset.desktopCloneReady === 'true') return;

      // Only clone if position is set to "media"
      if (block.dataset.supportPosition === 'details') {
        block.dataset.desktopCloneReady = 'true';
        return;
      }

      const section = block.closest('.product-information__media, .product-information__grid, .shopify-section');
      const host =
        section?.querySelector('[data-support-features-host]') ||
        document.querySelector('[data-support-features-host]');

      if (!host) return;

      const clone = block.cloneNode(true);
      clone.classList.add('is-desktop-clone');
      clone.removeAttribute('data-support-features-block');
      // Copy inline styles (including padding variables)
      clone.style.cssText = block.style.cssText;
      host.innerHTML = '';
      host.appendChild(clone);
      clone.querySelectorAll('.product-support-feature').forEach((feature) => {
        const trigger = feature.querySelector('.product-support-feature__trigger');
        const dialog = feature.querySelector('dialog');
        const closeButton = feature.querySelector('.product-support-feature__close');

        if (!trigger || !dialog) return;

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          if (!dialog.open) {
            dialog.showModal();
          }
        });

        closeButton?.addEventListener('click', (event) => {
          event.preventDefault();
          if (dialog.open) {
            dialog.close();
          }
        });

        dialog.addEventListener('click', (event) => {
          const rect = dialog.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;

          if (!inside && dialog.open) {
            dialog.close();
          }
        });
      });
      block.dataset.desktopCloneReady = 'true';
    });
  }

  function init() {
    document.querySelectorAll('[data-product-media-showcase]').forEach((root) => {
      if (root.dataset.showcaseInit === 'true') return;
      root.dataset.showcaseInit = 'true';
      new ProductMediaShowcase(root);
    });

    syncSupportFeatures();
  }

  function getMobileGalleryIndex(gallery) {
    if (!gallery) return 0;

    const selectedThumbs = Array.from(
      gallery.querySelectorAll('.media-gallery__mobile-controls .slideshow-controls__thumbnail')
    );
    const activeThumbIndex = selectedThumbs.findIndex(
      (thumb) =>
        thumb.getAttribute('aria-selected') === 'true' ||
        thumb.getAttribute('aria-current') === 'true' ||
        thumb.classList.contains('is-active') ||
        thumb.classList.contains('current')
    );
    if (activeThumbIndex >= 0) return activeThumbIndex;

    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const visibleSlideIndex = slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true');
    return visibleSlideIndex >= 0 ? visibleSlideIndex : 0;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mobile-showcase-action]');
    if (!button) return;

    const gallery = button.closest('media-gallery');
    const showcaseRoot = gallery?.querySelector('[data-product-media-showcase]');
    const showcase = showcaseRoot?.productMediaShowcase;
    if (!showcase) return;

    event.preventDefault();
    event.stopPropagation();

    const currentIndex = getMobileGalleryIndex(gallery);
    showcase.show(currentIndex, false);

    const action = button.dataset.mobileShowcaseAction;
    if (action === '360') {
      if (showcase.viewer) {
        showcase.viewer.reset({
          previewUrl: showcase.activePreviewUrl,
          framesRaw: showcase.active360FramesRaw,
        });
        showcase.viewer.showHint();
      }
      openDialog(showcase.viewerDialog);
      return;
    }

    if (action === 'video') {
      openDialog(showcase.videoDialog);
      window.setTimeout(() => {
        const video = showcase.videoDialog?.querySelector('video');
        if (video) {
          video.setAttribute('controls', 'controls');
          video.controls = true;
          video.currentTime = 0;
          video.play().catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }

        const iframe = showcase.videoDialog?.querySelector('iframe');
        if (iframe) {
          const autoplaySrc = iframe.dataset.autoplaySrc || iframe.dataset.baseSrc || iframe.src;
          if (autoplaySrc) iframe.src = autoplaySrc;
        }
      }, 120);
      return;
    }

    if (action === 'zoom') {
      showcase.showFullscreen(currentIndex, false);
      openDialog(showcase.fullscreenDialog);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const observer = new MutationObserver(() => {
    init();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      document.querySelectorAll('[data-video-dialog] video').forEach((video) => {
        video.pause();
      });
    }
  });
})();
